import { InferGetStaticPropsType } from 'next'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

/* blocks */
import Layout from '@/cmp/templates/Layout/Layout'
import PageHero from '@/cmp/organism/PageHero/PageHero'
import CustomerLogo from '@/cmp/organism/CustomerLogo/CustomerLogo'
import ValueOutcomes from '@/cmp/organism/ValueOutcomes/ValueOutcomes'
import ServicePicker from '@/cmp/templates/ContentTypes/components/ServicePicker/ServicePicker'
import ContentTypeFeatures from '@/cmp/organism/ContentTypeFeatures/ContentTypeFeatures'
import CaseStudies from '@/cmp/templates/ContentTypes/components/CaseStudies/CaseStudies'
import FAQNew from '@/cmp/organism/FAQNew/FAQNew'
import FooterCta from '@/cmp/organism/FooterCta/FooterCta'

/* utils */
import { getStrapiGraphqlData } from '@/utils/api/wrapper'
import {
  INSTANT_REVALIDATION,
  SITEMAP_REVALIDATE_TIME
} from '@/utils/constants/revalidation'
import { constructMetadataFromStrapiData } from '@/utils/functions/strapi'
import { translationPageSitemapQuery } from '@/utils/queries/sitemap'
import { capture404PageError } from '@/utils/functions/elk-server'
import {
  TranslationOverviewPageResponse,
  translationOverviewPageQuery
} from '@/utils/queries/translation_page'

/* assets */
import { ReactComponent as LoaderIcon } from 'public/assets/icons/loader.svg'

/* styles */
import styles from '../styles/translation.module.css'

function TranslationSitemapPage({
  data,
  caseStudies
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const translationPageData = useInfiniteQuery(
    ['translationPage'],
    async ({ pageParam = 1 }) =>
      await getStrapiGraphqlData<TranslationSitemapPageResponse>(
        translationPageSitemapQuery.loc?.source.body,
        { page: pageParam, limit: 100 }
      ),
    {
      enabled: true,
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
      getNextPageParam: lastPage => {
        const pagination = lastPage.translationPages.meta.pagination
        return pagination.pageCount > pagination.page
          ? pagination.page + 1
          : false
      }
    }
  )

  const translationPageLinkDetails = useMemo(() => {
    const translationPages =
      translationPageData.data?.pages.flatMap(pageData =>
        pageData.translationPages.data.map(i => i.attributes)
      ) ?? []

    return translationPages.map(translationPage => ({
      name: translationPage.page_title,
      slug: `/translation/${translationPage.source_language.data.attributes.iso_code}-${translationPage.slug}/`
    }))
  }, [translationPageData])

  return (
    <Layout
      title="Pepper Content - World's Largest Content Marketplace for Businesses and Creators"
      meta={constructMetadataFromStrapiData(data.seo)}
      canonical="/translation/"
    >
      <PageHero
        layout="left"
        class={styles.pageHero}
        primaryHeadline={data.hero.title}
        subText={data.hero.description}
        primaryCta={data.hero.primaryCta}
        image={data.hero.image?.data?.attributes}
      />

      {data.brandList.brands.data.length > 0 ? (
        <CustomerLogo
          title={data.brandList.title}
          brands={data.brandList.brands.data.map(i => ({
            ...i.attributes.logo.data.attributes,
            alternativeText: i.attributes.name
          }))}
        />
      ) : null}

      {data.goalList.length > 0 ? (
        <ValueOutcomes
          layout="descriptive"
          theme="dark"
          title={data.goalTitle}
          list={data.goalList.map(i => ({
            id: i.id,
            title: i.title ?? '',
            description: i.description ?? '',
            image: i.multimedia?.media?.data?.attributes
          }))}
        />
      ) : null}

      {data.services.length > 0 ? (
        <ServicePicker
          title={data.servicesTitle}
          description={data.servicesDescription}
          services={data.services}
        />
      ) : null}

      {data.contentTypeFeatureList.length > 0 ? (
        <ContentTypeFeatures
          title={data.contentTypeFeatureTitle}
          list={data.contentTypeFeatureList.map(i => ({
            id: i.id,
            link: i.link,
            bgColor: i.color,
            title: i.title ?? '',
            description: i.description ?? '',
            image: i.multimedia?.media?.data?.attributes,
            tag: i.additionalFields?.tag as string | null,
            color: i.additionalFields?.color as string | null
          }))}
        />
      ) : null}

      {caseStudies.length > 0 ? (
        <CaseStudies
          title={data.caseStudyTitle}
          data={caseStudies}
          mode="light"
        />
      ) : null}

      {data.faqs.length > 0 ? (
        <FAQNew title="Frequently asked questions" list={data.faqs} />
      ) : null}

      {data.footerCta ? (
        <FooterCta
          headline={data.footerCta.title}
          mode="full_width"
          primaryCta={data.footerCta.primaryCta}
          secondaryCta={data.footerCta.secondaryCta}
        />
      ) : null}

      <div className="max-desktop-width  | mx-auto py-5 px-5 sm:px-10 lg:px-6">
        <section className="py-5 text-justify">
          <h2 className="h5 font-medium  |  mb-1">Translations Page</h2>
          <div className="mb-3">
            {translationPageLinkDetails.map((link, index) => {
              return (
                <span key={link.name} className="text-small">
                  {index !== 0 && ' | '}
                  <a href={link.slug} className="hover:underline">
                    {link.name}
                  </a>
                </span>
              )
            })}
          </div>
          {translationPageData.hasNextPage && (
            <div className="flex justify-end items-center">
              <hr className="w-full text-blue-web-50" />
              <button
                onClick={() => translationPageData.fetchNextPage()}
                className="text-blue-web-50 font-medium | flex flex-nowrap flex-grow flex-shrink-0 | bg-transparent rounded-md | pl-2 pr-0"
              >
                View More{' '}
                {translationPageData.isFetchingNextPage ? (
                  <LoaderIcon className="animate-spin | ml-2" />
                ) : null}
              </button>
            </div>
          )}
        </section>
      </div>
    </Layout>
  )
}

export const getStaticProps = async () => {
  const result = await getStrapiGraphqlData<TranslationOverviewPageResponse>(
    translationOverviewPageQuery.loc?.source.body
  )

  if (!result || !result.page.data) {
    capture404PageError(`/translation/`)
    return { notFound: true, revalidate: INSTANT_REVALIDATION }
  }

  return {
    props: {
      data: result.page.data.attributes,
      caseStudies: result?.caseStudies?.data
    },
    revalidate: SITEMAP_REVALIDATE_TIME
  }
}

export default TranslationSitemapPage
